import { 
    Clock, 
    DollarSign, 
    Code2, 
    Users, 
    Rocket,
    Building2,
    Layers,
    BarChart3,
    ChevronRight,
  } from 'lucide-react';

const SolutionsSection = () => {
    const startupChallenges = [
      {
        icon: <Clock className="w-6 h-6" />,
        problem: "6-month dev cycles killing your runway",
        solution: "Ship your MVP in 8 weeks with weekly demos",
        metric: "75% faster",
        gradient: "from-blue-500 to-indigo-600"
      },
      {
        icon: <DollarSign className="w-6 h-6" />,
        problem: "Burning $100k before getting to market",
        solution: "Fixed-price MVPs starting at $15k",
        metric: "80% savings",
        gradient: "from-green-500 to-emerald-600"
      },
      {
        icon: <Code2 className="w-6 h-6" />,
        problem: "No technical co-founder to guide you",
        solution: "Senior developers as your tech partners",
        metric: "24/7 support",
        gradient: "from-purple-500 to-pink-600"
      }
    ];
  
    const smeChallenges = [
      {
        icon: <Layers className="w-6 h-6" />,
        problem: "Disconnected systems slowing you down",
        solution: "Unified platform connecting all operations",
        metric: "40% efficiency",
        gradient: "from-purple-500 to-pink-600"
      },
      {
        icon: <Users className="w-6 h-6" />,
        problem: "Losing customers to modern competitors",
        solution: "Customer portals & mobile experiences",
        metric: "2x retention",
        gradient: "from-pink-500 to-red-600"
      },
      {
        icon: <BarChart3 className="w-6 h-6" />,
        problem: "Making decisions on gut feel, not data",
        solution: "Real-time dashboards & analytics",
        metric: "30% growth",
        gradient: "from-orange-500 to-red-600"
      }
    ];
  
    return (
      <section className="py-6 md:py-24 bg-slate-50">
        <div className="container mx-auto px-4 md:px-6">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="pt-4 text-2xl md:text-4xl md:text-5xl font-bold mb-4 text-gray-900">
              We Solve Your Biggest 
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600"> Tech Challenges</span>
            </h2>
            <p className="px-4 md:text-xl text-gray-600">
              Whether you're building from scratch or modernizing existing systems
            </p>
          </div>
  
          <div className="max-w-7xl mx-auto">
            <div className='md:flex w-full justify-between'>
            {/* Startup Challenges */}
            <div className="mb-16 w-full p-4">
              <div className="md:flex items-center justify-center mb-8">
                <div className="flex items-center gap-3 bg-gradient-to-r from-blue-500 to-indigo-600 text-white px-6 py-3 rounded-full shadow-lg">
                  <Rocket className="w-5 h-5" />
                  <h3 className="md:text-lg font-semibold">For Startups: From 0 to 1</h3>
                </div>
              </div>
              
              <div className="grid md:grid-cols-1 gap-6 mb-8">
                {startupChallenges.map((item, index) => (
                  <div key={index} className="group relative">
                    <div className={`absolute inset-0 bg-gradient-to-r ${item.gradient} opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-2xl blur-xl`}></div>
                    <div className="relative bg-white rounded-2xl p-6 shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100 hover:border-transparent h-full">
                      <div className={`w-12 h-12 rounded-xl bg-gradient-to-r ${item.gradient} text-white flex items-center justify-center mb-4 shadow-lg`}>
                        {item.icon}
                      </div>
                      
                      <div className="mb-4">
                        <h4 className="font-bold text-gray-900 mb-2 text-base">The Problem:</h4>
                        <p className="text-gray-600 text-sm">{item.problem}</p>
                      </div>
                      
                      <div className="mb-4">
                        <h4 className="font-bold text-gray-900 mb-2 text-base">Our Solution:</h4>
                        <p className="text-gray-700 font-medium text-sm">{item.solution}</p>
                      </div>
                      
                      <div className="pt-4 border-t border-gray-100 mt-auto">
                        <div className="flex items-center justify-between">
                          <span className={`text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r ${item.gradient}`}>
                            {item.metric}
                          </span>
                          <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-gray-600 group-hover:translate-x-1 transition-all" />
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
  
            {/* SME Challenges */}
            <div className='mb-16 w-full p-4'>
              <div className="flex items-center justify-center mb-8">
                <div className="flex items-center gap-3 bg-gradient-to-r from-purple-500 to-pink-600 text-white px-6 py-3 rounded-full shadow-lg">
                  <Building2 className="w-5 h-5" />
                  <h3 className="md:text-lg font-semibold">For SMEs: Digital Transformation</h3>
                </div>
              </div>
              
              <div className="grid md:grid-cols-1 gap-6">
                {smeChallenges.map((item, index) => (
                  <div key={index} className="group relative">
                    <div className={`absolute inset-0 bg-gradient-to-r ${item.gradient} opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-2xl blur-xl`}></div>
                    <div className="relative bg-white rounded-2xl p-6 shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100 hover:border-transparent h-full">
                      <div className={`w-12 h-12 rounded-xl bg-gradient-to-r ${item.gradient} text-white flex items-center justify-center mb-4 shadow-lg`}>
                        {item.icon}
                      </div>
                      
                      <div className="mb-4">
                        <h4 className="font-bold text-gray-900 mb-2 text-base">The Problem:</h4>
                        <p className="text-gray-600 text-sm">{item.problem}</p>
                      </div>
                      
                      <div className="mb-4">
                        <h4 className="font-bold text-gray-900 mb-2 text-base">Our Solution:</h4>
                        <p className="text-gray-700 font-medium text-sm">{item.solution}</p>
                      </div>
                      
                      <div className="pt-4 border-t border-gray-100 mt-auto">
                        <div className="flex items-center justify-between">
                          <span className={`text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r ${item.gradient}`}>
                            {item.metric}
                          </span>
                          <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-gray-600 group-hover:translate-x-1 transition-all" />
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            </div>
            
          </div>
        </div>
      </section>
    );
  };
  

  export default SolutionsSection;